import type { ReactNode } from "react";
import { ROLE_LABELS, type MemberRole } from "@creators/backend/lib/access/roles";
import { Callout } from "@frontend/components/ui";

/**
 * Stands in for a form the viewer's role cannot use. Naming the role matters: it
 * turns "this is broken" into "ask the owner", which is an action they can take.
 */
export function RoleLocked({ role, children }: { role: MemberRole | null; children: ReactNode }) {
  return (
    <Callout tone="neutral" title={role ? `Read-only for a ${ROLE_LABELS[role]}` : "Read-only"}>
      {children}
    </Callout>
  );
}

/**
 * One titled block of settings. Every section page is a stack of these.
 *
 * `footer` is where a save button belongs: a settings form that scrolls should
 * keep its commit action pinned to the bottom of its own card, not floating in
 * the middle of the page where it reads as unrelated to anything.
 */
export function SettingsCard({
  title,
  description,
  action,
  footer,
  children,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="card overflow-hidden shadow-sm shadow-ink/[0.02]">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line/80 px-4 py-3.5 sm:px-5">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold tracking-tight text-ink">{title}</h2>
          {description ? (
            <p className="mt-1 text-sm leading-relaxed text-ink-muted">{description}</p>
          ) : null}
        </div>
        {action ? <div className="w-full shrink-0 sm:w-auto">{action}</div> : null}
      </div>
      <div className="px-4 py-4 sm:px-5">{children}</div>
      {footer ? (
        <div className="flex flex-col gap-3 border-t border-line/80 bg-canvas/40 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:px-5">
          {footer}
        </div>
      ) : null}
    </section>
  );
}

/**
 * The standard settings row: what the thing is on the left, its current state
 * and the control that changes it on the right. Stacks on narrow screens rather
 * than squeezing a control against a label.
 */
export function SettingsRow({
  label,
  description,
  children,
  icon,
}: {
  label: string;
  description?: string;
  children?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2.5 py-3 first:pt-0 last:pb-0 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-4">
      <div className="flex min-w-0 items-start gap-3">
        {icon ? (
          <span className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-canvas text-ink-muted ring-1 ring-line/70">
            {icon}
          </span>
        ) : null}
        <div className="min-w-0">
          <p className="text-sm font-medium text-ink">{label}</p>
          {description ? (
            <p className="mt-0.5 text-xs leading-relaxed text-ink-muted">{description}</p>
          ) : null}
        </div>
      </div>
      {children ? (
        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:shrink-0 sm:justify-end">
          {children}
        </div>
      ) : null}
    </div>
  );
}

/** A stack of settings rows with hairlines between them. */
export function SettingsRows({ children }: { children: ReactNode }) {
  return <div className="divide-y divide-line/70">{children}</div>;
}

/** A read-only fact about the account, shown next to the things you can edit. */
export function ReadOnlyRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <dt className="text-sm text-ink-muted">{label}</dt>
      <dd className="text-sm font-medium text-ink">{value}</dd>
    </div>
  );
}

/** A labelled on/off switch backed by a plain checkbox, so forms work uncontrolled. */
export function ToggleRow({
  name,
  label,
  hint,
  defaultChecked,
}: {
  name: string;
  label: string;
  hint?: string;
  defaultChecked?: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-xl px-1 py-2 transition hover:bg-canvas/60">
      <input
        type="checkbox"
        name={name}
        defaultChecked={defaultChecked}
        className="mt-0.5 size-4 shrink-0 rounded border-line-strong text-brand-600 focus:ring-2 focus:ring-brand-100"
      />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-ink">{label}</span>
        {hint ? <span className="mt-0.5 block text-xs text-ink-muted">{hint}</span> : null}
      </span>
    </label>
  );
}
